import React from "react";

import { animated, useSpring } from "@react-spring/web";

import type { SelectChangeEvent } from "@mui/material";

import AccessSelector from "@app/common/access/ShareReport/AccessSelector";
import type {
  ACLResponse,
  EntityAccessLevel,
  ShareEntity,
  WorkspaceEntity,
} from "@app/common/types";
import useWorkspace from "@app/common/workspaces/useWorkspace";

import AddShareEntitiesInput from "@rui/AddShareEntitiesInput";
import { Row } from "@rui/Stack";

import { ShareInputWrapper } from "./styles";
import WorkspaceShareSuggestions from "./WorkspaceShareSuggestions";

const AnimatedRow = animated(Row);

interface ShareReportAddEntitiesProps {
  accessList?: ACLResponse;
  bulkAccess: EntityAccessLevel;
  fullScreen?: boolean;
  isAddingEntities: boolean;
  onChangeBulkAccess: (value: EntityAccessLevel) => void;
  onInputChange: (selectedEntities: ShareEntity[]) => void;
  onSelectSuggestion: (entity: WorkspaceEntity) => void;
  selectedEntities: ShareEntity[];
}

const ShareReportAddEntities = ({
  bulkAccess,
  accessList,
  fullScreen = false,
  isAddingEntities,
  onChangeBulkAccess,
  onInputChange,
  onSelectSuggestion,
  selectedEntities,
}: ShareReportAddEntitiesProps) => {
  const { inWorkspace, workspace } = useWorkspace();

  const autoCompleteStyle = useSpring({
    width: isAddingEntities ? (fullScreen ? "100%" : "65%") : "100%",
    marginRight: isAddingEntities ? (fullScreen ? "0px" : "8px") : "0px",
    delay: isAddingEntities ? 0 : 400,
    config: {
      tension: 500,
    },
  });

  const accessSelectorProps = useSpring({
    opacity: isAddingEntities ? 1 : 0,
    delay: isAddingEntities ? 0 : 400,
    display: isAddingEntities ? "flex" : "none",
    config: {
      tension: 500,
    },
  });

  const handleChangeBulkAccess = (e: SelectChangeEvent) => {
    const accessLevel = e.target.value as EntityAccessLevel;
    onChangeBulkAccess(accessLevel);
  };

  return (
    <>
      <ShareInputWrapper spacing={{ xxs: "12px", sm: "0px" }}>
        <AnimatedRow variant="left" style={autoCompleteStyle}>
          <AddShareEntitiesInput
            accessList={accessList}
            emailOptionPrefix="Invite: "
            onChange={onInputChange}
            selectedEntities={selectedEntities}
            workspaceSearchParams={{ workspaceId: workspace.id }}
          />
        </AnimatedRow>
        <AnimatedRow
          flexGrow={1}
          flexShrink={1000}
          sx={{ overflow: "hidden", pt: "2px", mt: "-2px" }}
          alignItems="flex-start"
          style={accessSelectorProps}
        >
          <AccessSelector
            value={bulkAccess}
            onChange={handleChangeBulkAccess}
            removable={false}
            sx={{ minWidth: "185px", maxHeight: "46px" }}
          />
        </AnimatedRow>
      </ShareInputWrapper>
      {inWorkspace && (
        <WorkspaceShareSuggestions
          accessList={accessList}
          onAddEntity={onSelectSuggestion}
          selectedEntities={selectedEntities}
          workspace={workspace}
        />
      )}
    </>
  );
};

export default ShareReportAddEntities;
